// src/pages/EventsPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { 
  FaHome, 
  FaChevronRight, 
  FaCalendarAlt, 
  FaMapMarkerAlt, 
  FaClock, 
  FaUsers,
  FaFilter,
  FaTimes,
  FaSearch,
  FaYoutube,
  FaExternalLinkAlt
} from 'react-icons/fa';
import Section from '../components/common/Section';
import Heading from '../components/common/Heading';
import Button from '../components/common/Button';
import EVENTS_DATA, { 
  getAllEvents, 
  getUpcomingEvents, 
  getPastEvents, 
  getFeaturedEvents, 
  getEventsByCategory
} from '../data/eventsdata';

const EventsPage = () => {
  // State for filtering and searching
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [viewMode, setViewMode] = useState('upcoming'); // 'upcoming' or 'past'
  
  // Format date for display
  const formatDate = (dateString) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  
  // Format time for display
  const formatTime = (dateString) => {
    const options = { hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleTimeString('en-US', options);
  };
  
  // Check if an event is upcoming
  const isUpcoming = (dateString) => {
    return new Date(dateString) > new Date();
  };
  
  // Filter events based on category, search term, and upcoming/past status
  useEffect(() => {
    let events = getAllEvents();
    
    const filtered = events.filter(event => {
      const matchesCategory = activeFilter === 'all' || event.category === activeFilter;
      const matchesSearch = 
        event.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
        event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (event.location && event.location.toLowerCase().includes(searchTerm.toLowerCase()));
        
      // Determine if event is upcoming or past
      const isPastEvent = event.past || !isUpcoming(event.date);
      const matchesTimeframe = 
        (viewMode === 'upcoming' && !isPastEvent) || 
        (viewMode === 'past' && isPastEvent);
      
      return matchesCategory && matchesSearch && matchesTimeframe;
    });
    
    setFilteredEvents(filtered);
  }, [activeFilter, searchTerm, viewMode]);
  
  // Effect to scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Get featured events
  const featuredEvents = getFeaturedEvents().filter(event => isUpcoming(event.date));
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  
  return (
    <>
      <Helmet>
        <title>Events - Afrovate Tech Hub</title>
        <meta name="description" content="Stay updated with Afrovate Tech Hub's upcoming events, workshops, and ceremonies. Join us to enhance your skills and network with industry professionals." />
      </Helmet>
      
      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-16 overflow-hidden"
        style={{
          backgroundImage: 'url(/images/events/events-hero-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Dark overlay for better text visibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-dark to-primary opacity-75"></div>
        
        {/* Content */}
        <div className="container mx-auto px-4 relative z-10">
          {/* Breadcrumbs */}
          <div className="flex items-center mb-8 text-white text-sm">
            <Link to="/" className="flex items-center hover:text-secondary transition-colors">
              <FaHome className="mr-2" />
              Home
            </Link>
            <FaChevronRight className="mx-2 text-xs text-gray-300" />
            <span className="text-secondary font-medium">Events</span>
          </div>
          
          <div className="text-center max-w-3xl mx-auto">
            <motion.h1 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-4xl md:text-5xl font-bold text-white mb-6"
            >
              Events & Activities
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-xl text-white text-opacity-90 mb-8"
            >
              Join us for certification ceremonies, career talks, workshops, and field trips.
              Stay connected with our community and enhance your skills.
            </motion.p>
            
            {/* Search Bar */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="relative max-w-xl mx-auto"
            >
              <input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full py-3 px-6 pr-12 bg-white bg-opacity-95 rounded-full shadow-md focus:outline-none focus:ring-2 focus:ring-secondary"
              />
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500">
                {searchTerm ? (
                  <button onClick={() => setSearchTerm('')} className="hover:text-primary">
                    <FaTimes />
                  </button>
                ) : (
                  <FaSearch />
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Featured Events Section */}
      {featuredEvents.length > 0 && (
        <Section background="white" padding="lg">
          <Heading level={2} align="center" className="mb-8" withAccent>
            Featured Events
          </Heading>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {featuredEvents.map((event) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-100 transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl"
              >
                <div className="relative h-[30rem] overflow-hidden">
                  <img
                    src={event.image}
                    alt={event.title}
                    className="w-full h-full object-cover transition-transform duration-500 transform hover:scale-110"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://placehold.co/800x400/1a237e/ffffff?text=${event.title.replace(/ /g, '+')}`;
                    }}
                  />
                  <div className="absolute top-0 left-0 bg-primary text-white px-4 py-2 m-3 rounded-md font-semibold flex items-center">
                    <FaCalendarAlt className="mr-2" />
                    {formatDate(event.date)}
                  </div>
                </div>
                
                <div className="p-6">
                  <span className="inline-block bg-blue-100 text-primary-dark text-xs font-medium rounded-full px-2 py-1 mb-3">
                    {EVENTS_DATA.categories.find(c => c.id === event.category)?.name || event.category.toUpperCase()}
                  </span>
                  <h3 className="text-2xl font-bold text-primary-dark mb-3">{event.title}</h3>
                  <p className="text-gray-600 mb-6">{event.description}</p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="flex items-start">
                      <FaMapMarkerAlt className="text-secondary mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{event.location}</span>
                    </div>
                    <div className="flex items-start">
                      <FaClock className="text-secondary mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{formatTime(event.date)}</span>
                    </div>
                    {event.speakers && event.speakers.length > 0 && (
                      <div className="flex items-start col-span-2">
                        <FaUsers className="text-secondary mt-1 mr-3 flex-shrink-0" />
                        <div>
                          <span className="text-gray-700 font-medium">Speakers:</span>
                          <div className="text-gray-600 text-sm">
                            {event.speakers.map((speaker, index) => (
                              <div key={index}>
                                {speaker.name} - {speaker.title}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex space-x-3">
                    <Button 
                      variant="primary" 
                      to={`/events/${event.id}`} 
                      className="flex-grow"
                    >
                      Event Details
                    </Button>
                    {event.registration?.required && (
                      <Button 
                        variant="secondary" 
                        to={`/events/register/${event.id}`} 
                        className="flex-grow"
                      >
                        Register Now
                      </Button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </Section>
      )}
      
      {/* Events Filtering */}
      <div className="bg-white border-t border-b border-gray-200 sticky top-16 z-20">
        <div className="container mx-auto px-4 py-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Category Filter */}
            <div className="flex items-center overflow-x-auto scrollbar-hide">
              <div className="flex items-center text-gray-700 mr-3">
                <FaFilter className="mr-2" /> Filter:
              </div>
              <select
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value)}
                className="bg-gray-50 border border-gray-300 text-gray-700 py-2 px-3 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {EVENTS_DATA.categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* View Mode Toggle */}
            <div className="flex justify-center">
              <div className="bg-gray-100 rounded-full p-1 inline-flex">
                <button
                  className={`px-4 py-2 rounded-full font-medium text-sm transition-colors ${
                    viewMode === 'upcoming'
                      ? 'bg-primary text-white'
                      : 'text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => setViewMode('upcoming')}
                >
                  Upcoming Events
                </button>
                <button
                  className={`px-4 py-2 rounded-full font-medium text-sm transition-colors ${
                    viewMode === 'past'
                      ? 'bg-primary text-white'
                      : 'text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => setViewMode('past')}
                >
                  Past Events
                </button>
              </div>
            </div>
            
            {/* Clear Filters */}
            <div className="flex justify-end">
              {(activeFilter !== 'all' || searchTerm) && (
                <button
                  onClick={() => {
                    setActiveFilter('all');
                    setSearchTerm('');
                  }}
                  className="text-primary font-medium hover:text-primary-dark flex items-center"
                >
                  <FaTimes className="mr-2" /> Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Events List */}
      <Section background="gray" padding="lg">
        <div className="mb-8">
          <Heading level={2}>
            {viewMode === 'upcoming' ? 'Upcoming Events' : 'Past Events'}
          </Heading>
          <p className="text-gray-600">
            {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''} found
            {searchTerm && ` matching "${searchTerm}"`}
            {activeFilter !== 'all' && ` in ${EVENTS_DATA.categories.find(c => c.id === activeFilter)?.name}`}
          </p>
        </div>
        
        {filteredEvents.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-md">
            <div className="text-gray-400 text-5xl mb-4">
              <FaCalendarAlt className="mx-auto" />
            </div>
            <h3 className="text-xl font-bold text-gray-700 mb-2">No Events Found</h3>
            <p className="text-gray-600 mb-6">
              We couldn't find any events matching your criteria.
            </p>
            <Button
              variant="primary"
              onClick={() => {
                setActiveFilter('all');
                setSearchTerm('');
              }}
            >
              View All Events
            </Button>
          </div>
        ) : (
          <motion.div
            className="space-y-6"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {filteredEvents.map((event) => (
              <motion.div
                key={event.id}
                variants={itemVariants}
                className={`bg-white rounded-lg shadow-md overflow-hidden border border-gray-100 transform transition-all duration-300 ${
                  expandedEvent === event.id ? 'ring-2 ring-primary' : 'hover:-translate-y-1 hover:shadow-lg'
                }`}
              >
                <div className="grid grid-cols-1 md:grid-cols-4">
                  {/* Event Image */}
                  <div className="md:col-span-1">
                    <div className="h-48 md:h-full object-cover relative group">
                      <img
                        src={event.image}
                        alt={event.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `https://placehold.co/400x300/1a237e/ffffff?text=${event.title.replace(/ /g, '+')}`;
                        }}
                      />
                      {event.videoUrl && (
                        <a 
                          href={event.videoUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        >
                          <div className="flex flex-col items-center text-white">
                            <FaYoutube className="text-4xl text-red-600 mb-2" />
                            <span className="text-sm">Watch Video</span>
                          </div>
                        </a>
                      )}
                    </div>
                  </div>
                  
                  {/* Event Details */}
                  <div className="md:col-span-3 p-6">
                    <div className="flex flex-col h-full">
                      <div className="mb-auto">
                        <div className="flex flex-wrap justify-between items-start mb-2">
                          <span className="inline-block bg-blue-100 text-primary-dark text-xs font-medium rounded-full px-2 py-1 mb-2">
                            {EVENTS_DATA.categories.find(c => c.id === event.category)?.name || event.category.toUpperCase()}
                          </span>
                          {event.attendees && (
                            <span className="text-sm text-gray-500">
                              {event.attendees} attendees
                            </span>
                          )}
                        </div>
                        
                        <h3 className="text-xl font-bold text-primary-dark mb-2">
                          {event.title}
                          {event.theme && (
                            <span className="ml-2 text-sm font-normal text-gray-500">
                              Theme: {event.theme}
                            </span>
                          )}
                        </h3>
                        
                        <p className="text-gray-600 mb-4">
                          {expandedEvent === event.id 
                            ? event.longDescription || event.description 
                            : `${event.description.substring(0, 150)}${event.description.length > 150 ? '...' : ''}`}
                        </p>
                        
                        {event.description.length > 150 && (
                          <button
                            onClick={() => setExpandedEvent(expandedEvent === event.id ? null : event.id)}
                            className="text-primary font-medium hover:text-primary-dark mb-4"
                          >
                            {expandedEvent === event.id ? 'Show Less' : 'Read More'}
                          </button>
                        )}
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                          <div className="flex items-center">
                            <FaCalendarAlt className="text-secondary mr-3" />
                            <span className="text-gray-700">{formatDate(event.date)}</span>
                          </div>
                          <div className="flex items-center">
                            <FaClock className="text-secondary mr-3" />
                            <span className="text-gray-700">{formatTime(event.date)}</span>
                          </div>
                          <div className="flex items-center">
                            <FaMapMarkerAlt className="text-secondary mr-3" />
                            <span className="text-gray-700">{event.location}</span>
                          </div>
                          
                          {/* Show video link if available */}
                          {event.videoUrl && (
                            <div className="flex items-center">
                              <FaYoutube className="text-red-600 mr-3" />
                              <a 
                                href={event.videoUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-primary hover:underline flex items-center"
                              >
                                Watch Video <FaExternalLinkAlt className="ml-1 text-xs" />
                              </a>
                            </div>
                          )}
                          
                          {expandedEvent === event.id && event.speakers && event.speakers.length > 0 && (
                            <div className="flex items-start sm:col-span-2">
                              <FaUsers className="text-secondary mt-1 mr-3 flex-shrink-0" />
                              <div>
                                <span className="text-gray-700 font-medium">Speakers:</span>
                                <div className="text-gray-600 text-sm">
                                  {event.speakers.map((speaker, index) => (
                                    <div key={index}>
                                      {speaker.name}{speaker.title && ` - ${speaker.title}`}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="mt-4 flex space-x-3">
                        <Button 
                          variant="primary" 
                          to={`/events/${event.id}`} 
                          className="flex-grow sm:flex-grow-0"
                        >
                          Event Details
                        </Button>
                        {!event.past && isUpcoming(event.date) && event.registration?.required && (
                          <Button 
                            variant="secondary" 
                            to={`/events/register/${event.id}`} 
                            className="flex-grow sm:flex-grow-0"
                          >
                            Register Now
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </Section>
      
      {/* Calendar Section */}
      {/* <Section background="white" padding="lg">
        <div className="text-center mb-12">
          <Heading level={2} align="center" className="mb-4" withAccent>
            Events Calendar
          </Heading>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Stay updated with our upcoming events schedule. Mark your calendar and join us!
          </p>
        </div>
        
        <div className="max-w-5xl mx-auto">
          <div className="bg-gray-50 p-6 rounded-lg shadow-md">
            <div className="text-center">
              <p className="text-gray-600 mb-4">
                Our full calendar is coming soon! In the meantime, you can browse our events list above.
              </p>
              <p className="text-gray-600 mb-6">
                Subscribe to our newsletter to receive regular updates about upcoming events.
              </p>
              <Button 
                variant="primary" 
                to="/contact#newsletter" 
              >
                Subscribe to Updates
              </Button>
            </div>
          </div>
        </div>
      </Section> */}
      
      {/* Host an Event Section */}
      <Section background="gradient-primary" padding="lg">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <Heading level={2} color="white" className="mb-6">
              Host an Event with Afrovate Tech Hub
            </Heading>
            <p className="text-white text-opacity-90 mb-4">
              Are you interested in hosting a workshop, career talk, or other event with Afrovate Tech Hub? 
              We collaborate with organizations, businesses, and institutions to organize events that benefit our community.
            </p>
            <p className="text-white text-opacity-90 mb-6">
              Whether you're looking to speak at one of our events, sponsor an activity, or co-host a workshop, 
              we'd love to hear from you. Contact us to discuss potential collaborations.
            </p>
            <Button 
              variant="light" 
              size="lg" 
              to="/contact" 
            >
              Contact Us
            </Button>
          </div>
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="bg-white p-6 rounded-lg shadow-xl"
          >
            <h3 className="text-xl font-bold text-primary-dark mb-4">Types of Events We Host</h3>
            <ul className="space-y-4">
              {EVENTS_DATA.categories.filter(cat => 
                  ['certification', 'workshop', 'lecture', 'field-trip', 'conference'].includes(cat.id)
                ).map((category, index) => (
                <li key={index} className="flex">
                  <div className="h-8 w-8 rounded-full bg-primary-light bg-opacity-20 flex items-center justify-center mr-3 flex-shrink-0">
                    {category.icon || <FaCalendarAlt className="text-primary" />}
                  </div>
                  <div>
                    <h4 className="font-bold text-primary-dark">{category.name}</h4>
                    <p className="text-gray-600">
                      {category.id === 'certification' ? 'Formal events celebrating the achievements of our trainees' :
                       category.id === 'workshop' ? 'Hands-on learning experiences in specific digital skills' :
                       category.id === 'lecture' ? 'Informative sessions on specialized technical topics' :
                       category.id === 'field-trip' ? 'Educational visits to relevant sites and facilities' :
                       category.id === 'conference' ? 'Gatherings focused on industry trends and innovations' :
                       'Events that bring together the Afrovate Tech Hub community'}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </Section>
    </>
  );
};

export default EventsPage;